import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import Breakpoint from '../../Breakpoint'
import { BadnetTournamentModel } from '../../types/badnetTournament.type'

const BadnetTournamentEvent = ({ tournament }: { tournament: BadnetTournamentModel }) => {
    const { t } = useTranslation('translation')

    const cleanHtml = (html: string | null): string => {
        if (!html) return ''
        return html
            .replace(/<[^>]*>/g, '')
            .replace(/schedule/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .trim()
    }

    const cleanDeadline = cleanHtml(tournament.deadline)
    const participantsText = tournament.participants || ''

    return (
        <TournamentContainer>
            <TournamentWrapper>
                <HeaderSection>
                    <TournamentTitle>{tournament.title}</TournamentTitle>
                    {tournament.bvaMemberCount !== undefined && tournament.bvaMemberCount > 0 && (
                        <BvaMemberBadge>
                            <MemberCountLarge>{tournament.bvaMemberCount}</MemberCountLarge>
                            <MemberText>{t('page.badnetTournament.bvaMembers')}</MemberText>
                        </BvaMemberBadge>
                    )}
                </HeaderSection>
                {tournament.fillPercentage !== undefined && (
                    <ProgressBarSection>
                        <ProgressBarContainer>
                            <ProgressBarStyled fillPercentage={tournament.fillPercentage}>
                                <ProgressFillStyled fillPercentage={tournament.fillPercentage} />
                            </ProgressBarStyled>
                            <ProgressInfo>
                                <ProgressText>
                                    {tournament.currentParticipants}/{tournament.maxParticipants ?? '?'}
                                </ProgressText>
                                {tournament.remainingPlaces !== undefined && (
                                    <RemainingPlaces>
                                        {tournament.remainingPlaces} {t('page.badnetTournament.remainingPlaces')}
                                    </RemainingPlaces>
                                )}
                            </ProgressInfo>
                        </ProgressBarContainer>
                    </ProgressBarSection>
                )}
                <TournamentMeta>
                    <MetaItem>
                        <MetaLabel>{t('page.badnetTournament.date')}:</MetaLabel>
                        <MetaValue>{tournament.date}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                        <MetaLabel>{t('page.badnetTournament.place')}:</MetaLabel>
                        <MetaValue>{tournament.place}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                        <MetaLabel>{t('page.badnetTournament.categories')}:</MetaLabel>
                        <MetaValue>{tournament.categories}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                        <MetaLabel>{t('page.badnetTournament.classifications')}:</MetaLabel>
                        <MetaValue>{tournament.classifications}</MetaValue>
                    </MetaItem>
                    {cleanDeadline && (
                        <MetaItem>
                            <MetaLabel>{t('page.badnetTournament.deadline')}:</MetaLabel>
                            <MetaValue>{cleanDeadline}</MetaValue>
                        </MetaItem>
                    )}
                    {participantsText && (
                        <MetaItem>
                            <MetaLabel>{t('page.badnetTournament.participants')}:</MetaLabel>
                            <MetaValue>{participantsText}</MetaValue>
                        </MetaItem>
                    )}
                </TournamentMeta>
                {tournament.promoted && <PromotedBadge>{t('page.badnetTournament.promoted')}</PromotedBadge>}
            </TournamentWrapper>
            <LinkWrapper>
                <LinkTournament href={tournament.url} target="_blank" rel="noreferrer">
                    {t('page.badnetTournament.register')}
                </LinkTournament>
                {tournament.helloassoUrl && (
                    <HelloAssoLink href={tournament.helloassoUrl} target="_blank" rel="noreferrer">
                        {t('page.badnetTournament.helloasso')}
                    </HelloAssoLink>
                )}
            </LinkWrapper>
        </TournamentContainer>
    )
}

const LinkWrapper = styled.div`
    position: absolute;
    bottom: 0;
    right: 8px;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        position: inherit;
    }
`

const LinkTournament = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--primary-color);
    color: white;
    text-decoration: none;
    height: 36px;
    border-radius: 4px;
    transition: all 0.4s;
    margin-bottom: 8px;
    padding: 0 8px;

    &:hover {
        opacity: 0.7;
        color: white;
    }
`

const HelloAssoLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #00a3e0;
    color: white;
    text-decoration: none;
    height: 36px;
    border-radius: 4px;
    transition: all 0.4s;
    margin-bottom: 8px;
    padding: 0 8px;

    &:hover {
        opacity: 0.7;
        color: white;
    }
`

const TournamentContainer = styled.article`
    position: relative;
    width: 100%;
    min-height: 200px;
    display: flex;
    background: white;
    padding: 16px;
    border-radius: 6px;
    margin-right: 5px;
    margin-bottom: 24px;
    box-sizing: border-box;
    overflow: hidden;

    &:hover {
        box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        margin-bottom: 16px;
        flex-direction: column;
        min-height: auto;
    }
`

const TournamentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-right: 16px;
    margin-bottom: 8px;
`

const TournamentTitle = styled.h2`
    font-size: 2rem;
    margin-top: 0;
    margin-bottom: 16px;
    color: var(--primary-color);
    font-family: 'Roboto Semi Bold';
`

const TournamentMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const MetaItem = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`

const MetaLabel = styled.span`
    font-weight: bold;
    color: #666;
    min-width: 120px;
`

const MetaValue = styled.span`
    color: #333;
`

const PromotedBadge = styled.span`
    display: inline-block;
    background-color: rgba(180, 0, 0, 0.1);
    color: var(--primary-color);
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 1.9rem;
    font-weight: bold;
    margin-top: 8px;
    align-self: flex-start;
`

const BvaMemberBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background-color: rgba(0, 163, 224, 0.15);
    color: #00a3e0;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 1.9rem;
    font-weight: bold;
    border: 2px solid #00a3e0;
    box-shadow: 0 2px 8px rgba(0, 163, 224, 0.3);
`

const MemberCountLarge = styled.span`
    font-size: 2.4rem;
    font-weight: 800;
    color: #00a3e0;
`

const MemberText = styled.span`
    font-size: 1.6rem;
    font-weight: 600;
`

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
`

const ProgressBarSection = styled.div`
    margin-bottom: 16px;
`

const ProgressBarContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const ProgressBarStyled = styled.div<{ fillPercentage: number }>`
    width: 100%;
    height: 12px;
    background-color: #e0e0e0;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
`

const ProgressFillStyled = styled.div<{ fillPercentage: number }>`
    height: 100%;
    width: ${(props) => props.fillPercentage}%;
    background-color: ${(props) => {
        if (props.fillPercentage >= 100) return '#8b0000'
        if (props.fillPercentage >= 75) return '#ff4444'
        if (props.fillPercentage >= 50) return '#ff8800'
        return '#00cc66'
    }};
    transition: width 0.3s ease;
    border-radius: 6px;
`

const ProgressInfo = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.5rem;
`

const ProgressText = styled.span`
    font-weight: 600;
    color: #333;
`

const RemainingPlaces = styled.span`
    font-weight: 500;
    color: #666;
`

export default BadnetTournamentEvent
